import { Card, CardContent, Box, Typography, Stack, Button, FormHelperText, CircularProgress } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from 'yup';
import { FC, useCallback, useEffect, useState } from "react";
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, Redirect } from 'react-router-dom';
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import TextField from "@/components/formik/TextField";
import LayoutAuth from "./LayoutAuth";
import Create from "@/controller/Create";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { RecPatch } from "@/dataProvider/tools";
import { addMinutes } from "date-fns/esm";
import { useResourceRegister } from "@/utils/resourceRegister";
import { useAuthState } from "ra-core";


const Device: FC = () => {
  useResourceRegister({ name: 'device-verify' })

  const onSuccess = useCallback(({data}:{data:RecPatch}) => {
    window.localStorage.setItem('_od_df_usercode_', JSON.stringify({ exp: addMinutes(new Date(), 5), usercode: (data as any).usercode }))
  }, [])
  
  return (
    <Create resource="device-verify" basePath="" onSuccess={onSuccess}>
      <DeviceBody />
    </Create>
  )
}

const DeviceBody: FC = () => {
  const initialValues = {
    usercode: '',
    submit: null
  }
  const performVerify = useSimpleSaveAction()
  const { authenticated } = useAuthState()
  
  const validationSchema =
    Yup.object().shape({
      usercode: Yup.string().max(9)
        .required('User Code is required')
    })
  const onSubmit: OnSubmit<typeof initialValues> = (values, {
    setErrors,
    setStatus,
    setSubmitting
  }) => {
    const options = {
      onSuccess: () => {
        setStatus({ success: true });
        setSubmitting(false);
      },
      onFailure: (err: any) => {
        setStatus({ success: false });
        setErrors({ submit: err.message });
        setSubmitting(false);
      } 
    }
    performVerify({ usercode: values.usercode }, options)
  }
  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, isSubmitting, status } = formikCtrl

  return (
    <LayoutAuth>
      <Helmet>
        <title>Device Verification | OnData</title>
      </Helmet>
      <Card role="main">
        <CardContent sx={{ display: 'flex', flexDirection: 'column', p: 4 }} >
          <Stack justifyContent="space-between" alignItems="center" sx={{ mb: 3 }} >
            <div>
              <Typography
                id="sign-in-title"
                color="textPrimary"
                gutterBottom
                variant="h4"
                tabIndex={-1}
                component="h1"
              >
                Device Verification
              </Typography>
            </div>
          </Stack>
          <Box sx={{ flexGrow: 1, mt: 3 }} >
            {!status?.success ?
                <OdForm formikCtrl={formikCtrl} >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    To verify and configure your device, 
                    please enter the User Code from the OnData Configurator below.
                  </Typography>
                  <TextField
                    label="User Code"
                    margin="normal"
                    name="usercode"
                  />
                  {errors.submit && (
                    <Box sx={{ mt: 3 }}>
                      <FormHelperText error>
                        {errors.submit}
                      </FormHelperText>
                    </Box>
                  )}
                  <Box sx={{ mt: 2 }}>
                    <Button
                      color="primary"
                      disabled={isSubmitting}
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      Next
                    </Button>
                  </Box>
                </OdForm>
              : !authenticated ?
                <Box>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    To complete the device verification process, you need to sign into OnData first. 
                    Please click on the button below to go to the Sign In page.
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/login?verify-device=true"
                    color="primary"
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                  >
                    Sign Me In
                  </Button>
                </Box>
              :
                <RedirectInSecs seconds={1} to="/device-consent" />
            }
          </Box>
        </CardContent>
      </Card>
      
    </LayoutAuth>
  )
}

export default Device;

const RedirectInSecs : FC<{ to: string, seconds: number }> = ({to, seconds}) => {
  const [counted, setCounted] = useState(false)
  useEffect(() => {
    setTimeout(() => setCounted(true), seconds)
  }, [])
  return (counted
    ? <Redirect to={to} />
    : <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
  )
}
